import { sanitizeDiacritics, toGreek, toGreeklish, toTransliteratedLatin } from 'greek-utils';

/**
 * Normalizes text for Greek-aware filtering by removing diacritics and creating
 * multiple representations of the text for comprehensive matching
 * @param text - The text to normalize
 * @returns Array of normalized text variations
 */
export function normalizeTextForSearch(text: string): string[] {
  if (!text) {
    return [''];
  }

  const lowerText = text.toLowerCase();
  const variations = new Set<string>();

  // Add original text (lowercased)
  variations.add(lowerText);

  // Add sanitized version (removes diacritics)
  const sanitized = sanitizeDiacritics(lowerText);
  variations.add(sanitized);

  // Add Greek version (converts Latin to Greek if needed)
  try {
    const greek = toGreek(lowerText);
    variations.add(greek);
    variations.add(sanitizeDiacritics(greek)); // Also add sanitized Greek
  } catch {
    // toGreek might fail for non-Latin text, that's okay
  }

  // Add Greeklish version (Greek to Latin characters)
  const greeklish = toGreeklish(lowerText);
  variations.add(greeklish);
  variations.add(sanitizeDiacritics(greeklish)); // Also add sanitized Greeklish

  // Add transliterated Latin version
  const transliterated = toTransliteratedLatin(lowerText);
  variations.add(transliterated);
  variations.add(sanitizeDiacritics(transliterated)); // Also add sanitized transliteration

  return Array.from(variations);
}

/**
 * Custom filter function for Greek-aware text filtering
 * This function normalizes both the search term and the data value
 * to support Greek characters, diacritics, and transliterations
 * @param row - The table row data
 * @param columnId - The column identifier
 * @param value - The filter value (search term)
 * @returns Boolean indicating if the row matches the filter
 */
export function greekAwareFilter(
  row: { getValue: (columnId: string) => unknown },
  columnId: string,
  value: string
): boolean {
  if (!value) {
    return true;
  }

  const cellValue = row.getValue(columnId);
  if (!cellValue) {
    return false;
  }

  const searchTerm = value.toLowerCase();
  const searchVariations = normalizeTextForSearch(searchTerm);
  const cellVariations = normalizeTextForSearch(String(cellValue));

  // Check if any search variation matches any cell variation
  return searchVariations.some((searchVar) =>
    cellVariations.some((cellVar) => cellVar.includes(searchVar))
  );
}

export function createVariationMatcher(searchTerm: string): (textVariations: string[]) => boolean {
  const searchVariations = normalizeTextForSearch(searchTerm.trim().toLowerCase());

  return (textVariations) =>
    searchVariations.some((searchVar) =>
      textVariations.some((textVar) => textVar.includes(searchVar))
    );
}

/**
 * Simple Greek-aware text matching for direct string comparison
 * @param text - The text to search in
 * @param searchTerm - The search term
 * @returns Boolean indicating if the text matches the search term
 */
export function greekAwareTextMatch(text: string, searchTerm: string): boolean {
  if (!text || !searchTerm || !searchTerm.trim()) {
    return false;
  }

  return createVariationMatcher(searchTerm)(normalizeTextForSearch(text));
}
